import React, {ReactNode, useState} from "react";
import {addResources, getNewResources, Resource, sumResources} from "../../enum/Resource";
import {Button, ButtonTypes} from "../../../react/components/ui/spectre/buttons";
import {Icon} from "../../../react/components/ui/spectre/icon";
import {calculateProduction, calculateROI, Config, explainNextStep, ROIInfo, StepEvaluationWithVillage, VillageState as VillageStateType} from "../../Calculator/ResourceUpgradeOrder";
import {ImageOasisGeneric} from "../../Components/ImageMisc/ImageOasis";
import Resources from "../../Components/Resources";
import {DetailModal} from "./DetailModal";

type Props = {
    config: Config,
    lastSteps: Array<StepEvaluationWithVillage>,
    isExpansion?: boolean,
    village?: VillageStateType,
    stepNumber?: number|string,
}

function generateLetter(index: number): string {
    const str = [];
    index = Math.round(index);
    do {
        const letterIndex = index % 26;
        str.push(String.fromCharCode(letterIndex + 97));
        index = Math.floor(index / 26);
    } while (index !== 0 && str.length < 10);
    return str.reverse().join('');
}

export function VillageState({config, lastSteps, isExpansion, village: _village, stepNumber}: Props): ReactNode {
    const [expanded, setExpanded] = useState<boolean>(false);
    const [detailModal, setDetailModal] = useState<boolean>(false);

    const village = lastSteps.length === 0 ? _village : (expanded ? lastSteps[0].village : lastSteps[lastSteps.length - 1].village);

    const levelPerResource = Object.fromEntries(
        Object.keys(getNewResources())
            .map((resource: Resource) => [
                resource,
                village.resourceLevel.filter((_, index) => village.resourceTypes[index] === resource),
            ])
    ) as Record<Resource, Array<number>>

    const collectiveCostRess = lastSteps.reduce((a, b) => addResources(a, b.step.costResource), getNewResources());
    const collectiveCostTime = lastSteps.reduce((a, b) => a + b.step.costTime, 0);

    function toggleExpand() {
        setExpanded(!expanded);
    }

    const returnOfInvestment: ROIInfo = calculateROI(config, expanded ? [lastSteps[0]] : lastSteps);
    const displayStepNumber = (expanded ? stepNumber + '1' : stepNumber);

    return <>
        <div className={`villageStateWrapper`}>
            <div className='text-right'>{displayStepNumber}</div>
            <div className={isExpansion || expanded ? 'expansionIndicatorColumn' : ''}>
                {!isExpansion && lastSteps.length > 1 &&
                    <Button onClick={toggleExpand} buttonTypes={[ButtonTypes.verySmall]}>
                        <Icon type={expanded ? 'arrow-down' : 'arrow-right'}/>
                    </Button>
                }
            </div>
            <div>
                {lastSteps.length > 0 &&
                    <Button buttonTypes={[ButtonTypes.verySmall]} onClick={() => setDetailModal(true)}>
                        <Icon type={'search'}/>
                    </Button>}
                {detailModal && <DetailModal onClose={() => setDetailModal(false)} config={config}
                                             explain={explainNextStep(config, lastSteps[0].previousVillage)}
                                             step={lastSteps[0]}/>}
            </div>
            <div>{levelPerResource[Resource.Wood].join(' ')}</div>
            <div>{levelPerResource[Resource.Clay].join(' ')}</div>
            <div>{levelPerResource[Resource.Iron].join(' ')}</div>
            <div>{levelPerResource[Resource.Crop].join(' ')}</div>
            {village.boosterLevel.slice(0, 3).map((level, index) =>
                <div className={!config.enableWCIBoosters ? 'text-gray' : ''} key={index}>{level}</div>)}
            {village.boosterLevel.slice(3).map((level, index) =>
                <div key={index}>{level}</div>)}
            <div>{village.oasisBuilding}</div>
            <div className={!config.enableWaterworks ? 'text-gray' : ''}>{village.waterworks}</div>
            <div>{village.oases.map((oasis, index) => <ImageOasisGeneric key={index} size={16}
                                                                         oasis={oasis}></ImageOasisGeneric>)}</div>
            <div><Resources resources={calculateProduction(config, village)}/></div>
            <div>{Math.round(expanded ? lastSteps[0].step.costTime : collectiveCostTime)}</div>
            <div>{returnOfInvestment === null ? '' : Math.round(returnOfInvestment.roi)}</div>
        </div>
        {expanded &&
            lastSteps
                .slice(1)
                .map((step, index) =>
                    <VillageState key={index} config={config} lastSteps={[step]} isExpansion={true} stepNumber={stepNumber + (index + 2).toString()}></VillageState>
                )
        }
    </>
}
